import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import apiClient from '../../../utils/axiosConfig';
import logger from '../../../utils/logger';
import { useAuth } from '../../../context/AuthContext';
import { DeleteConfirmDialog } from '../DeleteConfirmDialog';
import { CloseButton } from '../CloseButton';
import { JOB_OPTIONS, jobLabel, isLeadership, normalizedJob } from '../../../lib/access';
import { ClientMark } from '../ClientMark';
import { AccessMatrix, mergeAccess } from '../AccessMatrix';
import { Plus, Search, Shield } from 'lucide-react';

const DEPT_LABEL = {
  administration: 'Leadership',
  marketing: 'Marketing',
  creative: 'Creative',
  technology: 'Technology',
  operations: 'Operations',
  external: 'Client',
};

const ROLE_COLOR = {
  owner: '#E8734A',
  admin: '#F59E0B',
  operations_manager: '#60A5FA',
  employee: '#A78BFA',
  client: '#4DD9FF',
};

function getRoleDisplay(u) {
  if (u.role === 'owner') return { label: 'Owner', color: ROLE_COLOR.owner };
  if (u.role === 'admin') return { label: 'Admin', color: ROLE_COLOR.admin };
  if (u.role === 'operations_manager') return { label: 'Operations Manager', color: ROLE_COLOR.operations_manager };
  if (u.role === 'client') return { label: 'Client', color: ROLE_COLOR.client };
  return { label: jobLabel(u), color: ROLE_COLOR.employee };
}

function optionForUser(user) {
  if (user.role === 'client') return 'client';
  if (user.role === 'admin') return 'admin';
  if (user.role === 'operations_manager') return 'operations_manager';
  if (user.job_role && JOB_OPTIONS.find((o) => o.value === user.job_role)) return user.job_role;
  if (user.job_role === 'custom' || user.sub_role) return 'custom';
  return 'junior_editor';
}

function payloadFromOption(form, selected) {
  if (selected.role === 'client') {
    return { role: 'client', job_role: 'client', client_id: form.client_id || null, assigned_client_ids: [] };
  }
  if (selected.role === 'admin' || selected.role === 'operations_manager') {
    return { role: selected.role, job_role: selected.value, client_id: null, assigned_client_ids: form.assigned_client_ids || [] };
  }
  return {
    role: 'employee',
    job_role: form.roleOption === 'custom' ? 'custom' : selected.value,
    sub_role: form.roleOption === 'custom' ? form.customRole.trim() : selected.value,
    client_id: null,
    assigned_client_ids: form.assigned_client_ids || [],
  };
}

function ClientAssignCheckboxes({ clients, selectedIds, onChange }) {
  const selected = selectedIds || [];
  const toggle = (id) => {
    if (selected.includes(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  };
  if (!clients.length) {
    return <p className="text-white/40 text-xs">Add clients first, then assign them here.</p>;
  }
  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <label className="text-white/40 text-[10px] uppercase tracking-widest">Assigned clients</label>
        <div className="flex gap-3 flex-shrink-0">
          <button type="button" className="text-[10px] text-[#E8734A] min-h-8 px-1" onClick={() => onChange(clients.map((c) => c.id))}>Select all</button>
          <button type="button" className="text-[10px] text-white/40 min-h-8 px-1" onClick={() => onChange([])}>Clear</button>
        </div>
      </div>
      <div className="max-h-48 overflow-y-auto rounded-xl border border-white/10 bg-white/[0.03] p-1.5 space-y-0.5">
        {clients.map((c) => {
          const on = selected.includes(c.id);
          return (
            <label key={c.id} className="flex items-center gap-3 px-2 min-h-11 rounded-lg hover:bg-white/[0.04] cursor-pointer">
              <input type="checkbox" checked={on} onChange={() => toggle(c.id)} className="accent-[#E8734A] w-4 h-4 flex-shrink-0" />
              <span className="text-white text-sm truncate">{c.name}</span>
              {c.industry && <span className="text-white/30 text-xs truncate ml-auto">{c.industry}</span>}
            </label>
          );
        })}
      </div>
      <p className="text-white/30 text-[10px] mt-1.5">{selected.length} client{selected.length === 1 ? '' : 's'} selected. Staff see only these.</p>
    </div>
  );
}

function CreateUserModal({ clients, onClose, onSave, actor, defaultRoleOption = 'junior_editor' }) {
  const [form, setForm] = useState({ name: '', email: '', password: '', roleOption: defaultRoleOption, customRole: '', client_id: '', assigned_client_ids: [] });
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inputCls = "w-full bg-white/[0.06] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-white/20 outline-none focus:border-[#E8734A]/50 transition-colors [&:-webkit-autofill]:shadow-[0_0_0_1000px_#0D0E1A_inset] [&:-webkit-autofill]:[-webkit-text-fill-color:white]";

  const options = JOB_OPTIONS.filter((o) => {
    if (o.role === 'admin') return actor?.role === 'owner';
    if (o.role === 'operations_manager') return actor?.role === 'owner' || actor?.role === 'admin';
    return true;
  });
  const selectedOpt = options.find((o) => o.value === form.roleOption) || options[0];
  const optStyle = { background: '#0D0E1A', color: '#fff' };

  const handleSave = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (form.roleOption === 'custom' && !form.customRole.trim()) {
      toast.error('Please enter a custom role');
      return;
    }
    if (selectedOpt.role === 'client' && !form.client_id) {
      toast.error('Please select a client profile for this user');
      return;
    }
    setSaving(true);
    try {
      const rolePayload = payloadFromOption(form, selectedOpt);
      const payload = {
        name: form.name,
        email: form.email,
        password: form.password,
        ...rolePayload,
      };
      logger.formSubmit('UsersView', 'create_user', { name: form.name, email: form.email, ...rolePayload });
      await apiClient.post('/users', payload);
      toast.success(`User "${form.name}" created successfully`);
      logger.success('New user created', { email: form.email, ...rolePayload });
      onSave();
      onClose();
    } catch (e) {
      const errorMsg = e.response?.data?.detail || 'Failed to create user';
      toast.error(errorMsg);
      logger.error('Failed to create user', { email: form.email, error: e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dash-overlay">
      <div className="dash-modal p-5 sm:p-6 w-full max-w-lg pb-[max(1.25rem,env(safe-area-inset-bottom))]">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-white font-medium">{defaultRoleOption === 'client' ? 'Create client login' : 'Add employee'}</h2>
          <CloseButton onClick={onClose} />
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-white/40 text-[10px] uppercase tracking-widest mb-1.5">Full Name</label>
            <input className={inputCls} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Rahul Sharma" />
          </div>
          <div>
            <label className="block text-white/40 text-[10px] uppercase tracking-widest mb-1.5">Email</label>
            <input className={inputCls} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="user@example.com" />
          </div>
          <div>
            <label className="block text-white/40 text-[10px] uppercase tracking-widest mb-1.5">Password</label>
            <div className="relative">
              <input
                className={inputCls + ' pr-14'}
                type={showPw ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => set('password', e.target.value)}
                placeholder="Min 6 characters"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#E8734A] hover:text-[#E8734A]/80 text-xs font-semibold transition-colors px-1 select-none"
              >
                {showPw ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-white/40 text-[10px] uppercase tracking-widest mb-1.5">Role / Type</label>
            <select className={inputCls} value={form.roleOption} onChange={(e) => set('roleOption', e.target.value)}>
              {['Leadership', 'Marketing', 'Creative', 'Technology', 'Operations', 'External'].map((g) => {
                const opts = options.filter((o) => o.group === g);
                if (!opts.length) return null;
                return (
                  <optgroup key={g} label={g} style={optStyle}>
                    {opts.map((o) => <option key={o.value} value={o.value} style={optStyle}>{o.label}</option>)}
                  </optgroup>
                );
              })}
            </select>
            {form.roleOption === 'custom' && (
              <input className={inputCls + ' mt-2'} value={form.customRole} onChange={(e) => set('customRole', e.target.value)} placeholder="e.g. Cleaner" />
            )}
          </div>
          {selectedOpt.role === 'client' && (
            <div>
              <label className="block text-white/40 text-[10px] uppercase tracking-widest mb-1.5">Link to Client Profile</label>
              <select className={inputCls} value={form.client_id} onChange={(e) => set('client_id', e.target.value)}>
                <option value="" style={optStyle}>— Select client —</option>
                {clients.map((c) => <option key={c.id} value={c.id} style={optStyle}>{c.name}</option>)}
              </select>
            </div>
          )}
          {selectedOpt.role === 'employee' && (
            <ClientAssignCheckboxes
              clients={clients}
              selectedIds={form.assigned_client_ids}
              onChange={(ids) => set('assigned_client_ids', ids)}
            />
          )}
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="dash-btn dash-btn-ghost flex-1">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="dash-btn dash-btn-primary flex-[2] h-10">
            {saving ? 'Creating…' : (defaultRoleOption === 'client' ? 'Create login' : 'Add employee')}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditUserModal({ editUser, clients, onClose, onSave, actor }) {
  const [form, setForm] = useState({
    name: editUser.name,
    roleOption: optionForUser(editUser),
    customRole: optionForUser(editUser) === 'custom' ? (editUser.job_title || editUser.sub_role || '') : '',
    client_id: editUser.client_id || '',
    assigned_client_ids: editUser.assigned_client_ids || [],
  });
  const [newPassword, setNewPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resettingPw, setResettingPw] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inputCls = "w-full bg-white/[0.06] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-white/20 outline-none focus:border-[#E8734A]/50 transition-colors [&:-webkit-autofill]:shadow-[0_0_0_1000px_#0D0E1A_inset] [&:-webkit-autofill]:[-webkit-text-fill-color:white]";

  const options = JOB_OPTIONS.filter((o) => {
    if (o.role === 'admin') return actor?.role === 'owner';
    if (o.role === 'operations_manager') return actor?.role === 'owner' || actor?.role === 'admin';
    return true;
  });
  const selectedOpt = options.find((o) => o.value === form.roleOption) || options[0];
  const optStyle = { background: '#0D0E1A', color: '#fff' };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (selectedOpt.role === 'client' && !form.client_id) {
      toast.error('Please select a client profile for this user');
      return;
    }
    if (form.roleOption === 'custom' && !form.customRole.trim()) { toast.error('Please enter a custom role'); return; }
    setSaving(true);
    try {
      const rolePayload = payloadFromOption(form, selectedOpt);
      const payload = { name: form.name, ...rolePayload };
      await apiClient.put(`/users/${editUser.id}`, payload);
      toast.success('User updated successfully');
      onSave();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setResettingPw(true);
    try {
      await apiClient.put(`/users/${editUser.id}/reset-password`, { password: newPassword });
      toast.success('Password reset successfully');
      setNewPassword('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPw(false);
    }
  };

  return (
    <div className="dash-overlay">
      <div className="dash-modal p-5 sm:p-6 w-full max-w-lg pb-[max(1.25rem,env(safe-area-inset-bottom))]">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-white font-medium">Edit user</h2>
          <CloseButton onClick={onClose} />
        </div>

        <div className="space-y-3 mb-4">
          <div>
            <label className="block text-white/40 text-[10px] uppercase tracking-widest mb-1.5">Full Name</label>
            <input className={inputCls} value={form.name} onChange={(e) => set('name', e.target.value)} />
          </div>
          <div>
            <label className="block text-white/40 text-[10px] uppercase tracking-widest mb-1.5">Email (cannot change)</label>
            <input className={inputCls + ' opacity-40 cursor-not-allowed'} value={editUser.email} readOnly />
          </div>
          <div>
            <label className="block text-white/40 text-[10px] uppercase tracking-widest mb-1.5">Role / Type</label>
            <select className={inputCls} value={form.roleOption} onChange={(e) => set('roleOption', e.target.value)}>
              {['Leadership', 'Marketing', 'Creative', 'Technology', 'Operations', 'External'].map((g) => {
                const opts = options.filter((o) => o.group === g);
                if (!opts.length) return null;
                return (
                  <optgroup key={g} label={g} style={optStyle}>
                    {opts.map((o) => <option key={o.value} value={o.value} style={optStyle}>{o.label}</option>)}
                  </optgroup>
                );
              })}
            </select>
            {form.roleOption === 'custom' && (
              <input className={inputCls + ' mt-2'} value={form.customRole} onChange={(e) => set('customRole', e.target.value)} placeholder="e.g. Cleaner" />
            )}
          </div>
          {selectedOpt.role === 'client' && (
            <div>
              <label className="block text-white/40 text-[10px] uppercase tracking-widest mb-1.5">Linked Client Profile</label>
              <select className={inputCls} value={form.client_id} onChange={(e) => set('client_id', e.target.value)}>
                <option value="" style={optStyle}>— Select client —</option>
                {clients.map((c) => <option key={c.id} value={c.id} style={optStyle}>{c.name}</option>)}
              </select>
            </div>
          )}
          {selectedOpt.role === 'employee' && editUser.role !== 'owner' && (
            <ClientAssignCheckboxes
              clients={clients}
              selectedIds={form.assigned_client_ids}
              onChange={(ids) => set('assigned_client_ids', ids)}
            />
          )}
        </div>

        <div className="flex gap-3 mb-5">
          <button onClick={onClose} className="dash-btn dash-btn-ghost flex-1">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="dash-btn dash-btn-primary flex-[2] h-10">
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>

        <div className="border-t border-white/[0.08] pt-4">
          <p className="text-white/30 text-[10px] uppercase tracking-widest mb-3">Reset Password</p>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                className={inputCls + ' pr-14'}
                type={showPw ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New password"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#E8734A] hover:text-[#E8734A]/80 text-xs font-semibold transition-colors px-1 select-none"
              >
                {showPw ? 'Hide' : 'Show'}
              </button>
            </div>
            <button onClick={handleResetPassword} disabled={resettingPw || newPassword.length < 6}
              className="bg-white/[0.08] border border-white/[0.12] text-white/70 text-sm font-semibold px-4 py-2.5 rounded-xl hover:bg-white/[0.14] disabled:opacity-40 transition-all whitespace-nowrap">
              {resettingPw ? '…' : 'Reset'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function departmentOf(u) {
  return DEPT_LABEL[u.department] || JOB_OPTIONS.find((o) => o.value === u.job_role)?.group || 'Team';
}

function joinedOn(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function linkedClientsOf(person, clients) {
  const ids = person.role === 'client'
    ? (person.client_id ? [person.client_id] : [])
    : (person.assigned_client_ids || []);
  return ids.map((id) => clients.find((c) => c.id === id)).filter(Boolean);
}

function PersonActions({ person, selfId, onEdit, onDelete, onAccess }) {
  if (person.id === selfId) {
    return <span className="text-white/30 text-[11px]">You</span>;
  }
  return (
    <div className="flex gap-1.5 flex-wrap justify-end">
      {person.role === 'employee' && onAccess && (
        <button type="button" onClick={() => onAccess(person)} className="dash-btn dash-btn-ghost dash-btn-sm">Pages</button>
      )}
      <button type="button" onClick={() => onEdit(person)} className="dash-btn dash-btn-ghost dash-btn-sm">Edit</button>
      {person.role !== 'owner' && (
        <button type="button" onClick={() => onDelete(person)} className="dash-btn dash-btn-danger dash-btn-sm">Remove</button>
      )}
    </div>
  );
}

function accessSummary(person, catalog) {
  if (!catalog || person.role !== 'employee') return null;
  const overrides = person.access_overrides || {};
  const n = Object.keys(overrides).length;
  if (!n) return 'Role default pages';
  return `${n} custom page${n === 1 ? '' : 's'}`;
}

function EmployeeAccessModal({ person, catalog, onClose, onSave }) {
  const pages = (catalog?.pages || []).filter((p) => p.staff);
  const defaults = catalog?.jobs?.[normalizedJob(person)] || {};
  const [value, setValue] = useState(() => mergeAccess(defaults, person.access_overrides));
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/users/${person.id}`, { access_overrides: value });
      toast.success(`Pages updated for ${person.name}`);
      onSave();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save page access');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => setValue({ ...defaults });

  return (
    <div className="dash-overlay">
      <div className="dash-modal p-5 sm:p-6 w-full max-w-lg pb-[max(1.25rem,env(safe-area-inset-bottom))]">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-white font-medium">Pages for {person.name}</h2>
          <CloseButton onClick={onClose} />
        </div>
        <p className="text-white/40 text-xs mb-4">
          Off hides the page. View is read-only. Edit lets them change things. Empty customisation follows their {jobLabel(person)} role.
        </p>
        <AccessMatrix pages={pages} value={value} defaults={defaults} onChange={setValue} audience="staff" />
        <div className="flex flex-wrap gap-3 mt-5">
          <button type="button" onClick={handleReset} className="dash-btn dash-btn-ghost">Reset to role default</button>
          <button type="button" onClick={onClose} className="dash-btn dash-btn-ghost ml-auto">Cancel</button>
          <button type="button" onClick={handleSave} disabled={saving} className="dash-btn dash-btn-primary h-10">
            {saving ? 'Saving…' : 'Save pages'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ClientAccessModal({ catalog, onClose, onSave }) {
  const pages = (catalog?.pages || []).filter((p) => p.client);
  const defaults = catalog?.client_default || {};
  const [value, setValue] = useState(() => ({ ...(catalog?.client || defaults) }));
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put('/access/client', { pages: value });
      toast.success('Client portal pages updated for every client login');
      onSave();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save client pages');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => setValue({ ...defaults });

  return (
    <div className="dash-overlay">
      <div className="dash-modal p-5 sm:p-6 w-full max-w-lg pb-[max(1.25rem,env(safe-area-inset-bottom))]">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-white font-medium">Client portal pages</h2>
          <CloseButton onClick={onClose} />
        </div>
        <p className="text-white/40 text-xs mb-4">
          One setting for every client login. Change it here and it applies to all clients — you cannot set this per client.
        </p>
        <AccessMatrix pages={pages} value={value} defaults={defaults} onChange={setValue} audience="client" />
        <div className="flex flex-wrap gap-3 mt-5">
          <button type="button" onClick={handleReset} className="dash-btn dash-btn-ghost">Reset to built-in default</button>
          <button type="button" onClick={onClose} className="dash-btn dash-btn-ghost ml-auto">Cancel</button>
          <button type="button" onClick={handleSave} disabled={saving} className="dash-btn dash-btn-primary h-10">
            {saving ? 'Saving…' : 'Save for all clients'}
          </button>
        </div>
      </div>
    </div>
  );
}

function LinkedClientList({ person, clients }) {
  const allAccess = isLeadership(person) || person.role === 'owner';
  if (allAccess) {
    return (
      <div className="rounded-lg bg-white/[0.04] border border-white/[0.06] px-3 py-2.5">
        <div className="text-[10px] uppercase tracking-widest text-white/30 mb-1">Access</div>
        <div className="text-white text-sm">Every client</div>
        <div className="text-white/35 text-xs mt-0.5">Leadership is not limited to an assignment list.</div>
      </div>
    );
  }
  const linked = linkedClientsOf(person, clients);
  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-widest text-white/30">Linked clients</div>
        <div className="text-[11px] text-white/40">{linked.length} assigned</div>
      </div>
      {linked.length === 0 ? (
        <div className="rounded-lg border border-dashed border-white/10 px-3 py-3 text-white/35 text-xs leading-relaxed">
          No clients linked. They cannot see client work until you assign them.
        </div>
      ) : (
        <div className="space-y-1.5 max-h-44 overflow-y-auto pr-0.5">
          {linked.map((c) => (
            <div key={c.id} className="flex items-center gap-2.5 rounded-lg bg-white/[0.04] border border-white/[0.06] px-2.5 py-2">
              <ClientMark client={c} size={28} />
              <div className="min-w-0 flex-1">
                <div className="text-white text-sm truncate">{c.name}</div>
                <div className="text-white/35 text-[11px] truncate">{c.industry || 'Client'}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmployeeCard({ person, clients, selfId, onEdit, onDelete, onAccess, accessHint }) {
  const rd = getRoleDisplay(person);
  const joined = joinedOn(person.created_at);
  return (
    <div className="dash-card p-5 flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-lg flex items-center justify-center text-white text-sm font-semibold flex-shrink-0 bg-navy border border-white/[0.08]">
          {person.name?.[0]?.toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-white font-medium truncate">{person.name}</div>
          <div className="text-white/40 text-xs truncate mt-0.5">{person.email}</div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            <span
              className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
              style={{ background: `${rd.color}18`, color: rd.color, border: `1px solid ${rd.color}40` }}
            >
              {rd.label}
            </span>
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] text-white/50 border border-white/10">
              {departmentOf(person)}
            </span>
          </div>
        </div>
      </div>
      <LinkedClientList person={person} clients={clients} />
      {accessHint && (
        <div className="text-white/35 text-[11px] -mt-1">{accessHint}</div>
      )}
      <div className="flex items-center justify-between gap-2 pt-1 border-t border-white/[0.06]">
        <div className="text-white/30 text-[11px]">{joined ? `Joined ${joined}` : 'Team member'}</div>
        <PersonActions person={person} selfId={selfId} onEdit={onEdit} onDelete={onDelete} onAccess={onAccess} />
      </div>
    </div>
  );
}

function ClientUserCard({ person, clients, selfId, onEdit, onDelete }) {
  const linked = linkedClientsOf(person, clients)[0];
  return (
    <div className="dash-card p-5 flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-lg flex items-center justify-center text-white text-sm font-semibold flex-shrink-0 bg-navy border border-white/[0.08]">
          {person.name?.[0]?.toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-white font-medium truncate">{person.name}</div>
          <div className="text-white/40 text-xs truncate mt-0.5">{person.email}</div>
          <div className="text-[10px] uppercase tracking-widest text-[#4DD9FF]/80 mt-2">Client login</div>
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/30 mb-2">Logs in as</div>
        {linked ? (
          <div className="flex items-center gap-2.5 rounded-lg bg-white/[0.04] border border-white/[0.06] px-2.5 py-2.5">
            <ClientMark client={linked} size={32} />
            <div className="min-w-0">
              <div className="text-white text-sm truncate">{linked.name}</div>
              <div className="text-white/35 text-[11px] truncate">{linked.industry || 'Sees only this client'}</div>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-white/10 px-3 py-3 text-white/35 text-xs">
            Not linked to a client profile.
          </div>
        )}
      </div>
      <div className="flex justify-end pt-1 border-t border-white/[0.06]">
        <PersonActions person={person} selfId={selfId} onEdit={onEdit} onDelete={onDelete} />
      </div>
    </div>
  );
}

export default function UsersView() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('employees');
  const [query, setQuery] = useState('');
  const [modal, setModal] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [accessUser, setAccessUser] = useState(null);
  const [clientAccessOpen, setClientAccessOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/users'),
      apiClient.get('/clients'),
      apiClient.get('/access/catalog').catch(() => ({ data: null })),
    ]).then(([ur, cr, ar]) => {
      setUsers((ur.data || []).filter((u) => u.is_active !== false));
      setClients(cr.data || []);
      setCatalog(ar.data || null);
      setLoading(false);
      logger.info('Users list loaded', { count: ur.data?.length || 0 });
    }).catch((e) => {
      logger.error('Failed to load users', { error: e.message });
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      logger.userAction('UsersView', 'delete_user', { userId: deleteConfirm.id, email: deleteConfirm.email });
      await apiClient.delete(`/users/${deleteConfirm.id}`);
      setUsers((u) => u.filter((x) => x.id !== deleteConfirm.id));
      toast.success(`Removed "${deleteConfirm.name}"`);
      logger.success('User deleted', { userId: deleteConfirm.id });
      setDeleteConfirm(null);
    } catch (e) {
      const errorMsg = e.response?.data?.detail || 'Failed to delete user';
      toast.error(errorMsg);
      logger.error('Failed to delete user', { userId: deleteConfirm.id, error: e.message });
    } finally {
      setDeleting(false);
    }
  };

  const matchQuery = (u) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const hay = [u.name, u.email, jobLabel(u), u.department, ...(u.assigned_client_ids || []).map((id) => clients.find((c) => c.id === id)?.name)].join(' ').toLowerCase();
    return hay.includes(q);
  };

  const leadership = users.filter((u) => isLeadership(u) || u.role === 'owner').filter(matchQuery);
  const employees = users.filter((u) => u.role === 'employee').filter(matchQuery);
  const clientUsers = users.filter((u) => u.role === 'client').filter(matchQuery);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="dash-title">Team & Users</h1>
          <p className="dash-sub">Employees are the floor. Users are client logins into the portal.</p>
        </div>
        <button onClick={() => setModal(true)} className="dash-btn dash-btn-primary self-start">
          <Plus size={14} strokeWidth={2} />
          {tab === 'users' ? 'Add client login' : 'Add employee'}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        {[
          { id: 'employees', label: 'Employees', count: users.filter((u) => u.role === 'employee').length },
          { id: 'users', label: 'Users', count: users.filter((u) => u.role === 'client').length },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-3 py-2 rounded-md text-sm ${tab === t.id ? 'bg-white/[0.08] text-white' : 'text-white/40'}`}
          >
            {t.label}
            <span className="ml-1.5 text-white/30">{t.count}</span>
          </button>
        ))}
        <div className="relative w-full sm:w-auto sm:ml-auto sm:min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            className="dash-input pl-9 h-10"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={tab === 'users' ? 'Search logins or clients' : 'Search employees or clients'}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-8 h-8 border-2 border-[#E8734A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : tab === 'employees' ? (
        <div className="space-y-8">
          {leadership.length > 0 && (
            <section>
              <h2 className="text-white/40 text-[11px] uppercase tracking-widest mb-3">Leadership</h2>
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {leadership.map((person) => (
                  <EmployeeCard
                    key={person.id}
                    person={person}
                    clients={clients}
                    selfId={user?.id}
                    onEdit={setEditModal}
                    onDelete={setDeleteConfirm}
                  />
                ))}
              </div>
            </section>
          )}
          <section>
            <h2 className="text-white/40 text-[11px] uppercase tracking-widest mb-3">Employees</h2>
            {employees.length === 0 ? (
              <div className="dash-card p-8 text-center">
                <p className="text-white/50 text-sm">No employees{query ? ' match that search' : ' yet'}.</p>
                <p className="text-white/30 text-xs mt-1.5">Add an editor, marketer, or developer and link the clients they work on.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {employees.map((person) => (
                  <EmployeeCard
                    key={person.id}
                    person={person}
                    clients={clients}
                    selfId={user?.id}
                    onEdit={setEditModal}
                    onDelete={setDeleteConfirm}
                    onAccess={setAccessUser}
                    accessHint={accessSummary(person, catalog)}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      ) : (
        <section>
          <div className="dash-card p-4 mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-white/[0.05] border border-white/[0.08] flex items-center justify-center flex-shrink-0">
              <Shield size={16} className="text-[#4DD9FF]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-white text-sm">Client portal pages</div>
              <div className="text-white/40 text-xs mt-0.5">
                One setting for every client login. {catalog?.client_custom ? 'Custom pages are in use.' : 'Using the built-in client defaults.'} You cannot change this per client.
              </div>
            </div>
            <button type="button" onClick={() => setClientAccessOpen(true)} className="dash-btn dash-btn-ghost self-start sm:self-center">
              Edit pages
            </button>
          </div>
          <h2 className="text-white/40 text-[11px] uppercase tracking-widest mb-3">Client logins</h2>
          {clientUsers.length === 0 ? (
            <div className="dash-card p-8 text-center">
              <p className="text-white/50 text-sm">No client users{query ? ' match that search' : ' yet'}.</p>
              <p className="text-white/30 text-xs mt-1.5">These are portal accounts. Each one logs in as a single client.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {clientUsers.map((person) => (
                <ClientUserCard
                  key={person.id}
                  person={person}
                  clients={clients}
                  selfId={user?.id}
                  onEdit={setEditModal}
                  onDelete={setDeleteConfirm}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {modal && (
        <CreateUserModal
          clients={clients}
          actor={user}
          defaultRoleOption={tab === 'users' ? 'client' : 'junior_editor'}
          onClose={() => setModal(false)}
          onSave={load}
        />
      )}

      {editModal && (
        <EditUserModal editUser={editModal} clients={clients} actor={user} onClose={() => setEditModal(null)} onSave={load} />
      )}

      {accessUser && catalog && (
        <EmployeeAccessModal person={accessUser} catalog={catalog} onClose={() => setAccessUser(null)} onSave={load} />
      )}

      {clientAccessOpen && catalog && (
        <ClientAccessModal catalog={catalog} onClose={() => setClientAccessOpen(false)} onSave={load} />
      )}

      {deleteConfirm && (
        <DeleteConfirmDialog
          title={deleteConfirm.role === 'client' ? 'Remove client login' : 'Remove employee'}
          message={`This removes "${deleteConfirm.name}" (${deleteConfirm.email}) from Team & Users. They will lose chat access and cannot sign in. This cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteConfirm(null)}
          isLoading={deleting}
        />
      )}
    </div>
  );
}
